import React, { useCallback, useEffect, useMemo, useState } from "react";
import PageTransition from "../components/PageTransition";
import axios from "axios";
import toast from "react-hot-toast";
import { BASE_ENDPOINT } from "../helpers/Config";
import { AnimatePresence, motion } from "framer-motion";
import {
    FaMoneyBillWave,
    FaUser,
    FaRupeeSign,
    FaSearch,
    FaSyncAlt,
    FaCopy,
    FaRegCommentDots,
    FaCalendarAlt,
    FaTimes,
    FaCheck,
    FaIdCard,
    FaPhone,
    FaEnvelope,
    FaCalendar,
    FaFileInvoiceDollar,
    FaUserCircle,
    FaInfoCircle,
} from "react-icons/fa";


const LIST_ENDPOINT = "/admin/bonus-list";
const GIVE_ENDPOINT = "/admin/give-bonus";
const PAGE_SIZE = 20;

/* ---------------- Helpers ---------------- */
function getTodayDate() {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
}

function safeText(v) {
    return v === null || v === undefined || v === "" ? "—" : String(v);
}

function nfmt(v, digits = 0) {
    const num = Number(v);
    if (!Number.isFinite(num)) return safeText(v);
    return new Intl.NumberFormat("en-IN", { maximumFractionDigits: digits }).format(num);
}

function moneyFmt(v, digits = 2) {
    const num = Number(v);
    if (!Number.isFinite(num)) return safeText(v);
    return new Intl.NumberFormat("en-IN", { maximumFractionDigits: digits }).format(num);
}

function toMySQLDate(dateInputValue) {
    const v = String(dateInputValue || "").trim();
    if (!v) return "";
    return /^\d{4}-\d{2}-\d{2}$/.test(v) ? v : "";
}

function pad2(n) {
    return String(n).padStart(2, "0");
}

function formatDateTime(d) {
    if (!d) return "—";
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return safeText(d);

    const DD = pad2(dt.getDate());
    const MM = pad2(dt.getMonth() + 1);
    const YYYY = dt.getFullYear();

    let hh = dt.getHours(); // 0-23
    const mm = pad2(dt.getMinutes());
    const ss = pad2(dt.getSeconds());

    const A = hh >= 12 ? "PM" : "AM";
    hh = hh % 12;
    if (hh === 0) hh = 12;

    return `${DD}/${MM}/${YYYY} ${pad2(hh)}:${mm}:${ss} ${A}`;
}

async function copyToClipboard(text) {
    try {
        if (navigator?.clipboard?.writeText) {
            await navigator.clipboard.writeText(String(text));
            return true;
        }
    } catch (_) { }
    try {
        const ta = document.createElement("textarea");
        ta.value = String(text);
        ta.style.position = "fixed";
        ta.style.top = "-1000px";
        ta.style.left = "-1000px";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(ta);
        return ok;
    } catch (_) {
        return false;
    }
}

function CopyBtn({ value, label = "Copied" }) {
    const disabled = value === null || value === undefined || value === "" || value === "—";
    const onCopy = async () => {
        const ok = await copyToClipboard(value);
        if (ok) toast.success(label);
        else toast.error("Copy failed");
    };
    return (
        <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            disabled={disabled}
            onClick={onCopy}
            title="Copy"
            style={{ padding: "2px 6px" }}
        >
            <FaCopy />
        </button>
    );
}

/* -------------- Pagination (select) -------------- */
function PaginationBar({ loading, pageNo, totalPages, onPrev, onNext, onJump }) {
    const pages = useMemo(() => {
        const n = Number(totalPages || 1);
        return Array.from({ length: n }, (_, i) => i + 1);
    }, [totalPages]);

    return (
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
            <div className="text-muted small">
                Page <span className="fw-semibold">{pageNo}</span> /{" "}
                <span className="fw-semibold">{totalPages}</span>
            </div>

            <div className="d-flex align-items-center gap-2">
                <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={onPrev}
                    disabled={loading || pageNo <= 1}
                    type="button"
                >
                    Prev
                </button>

                <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={onNext}
                    disabled={loading || pageNo >= totalPages}
                    type="button"
                >
                    Next
                </button>

                <select
                    className="form-select form-select-sm"
                    style={{ width: 120 }}
                    value={pageNo}
                    onChange={(e) => onJump(Number(e.target.value))}
                    disabled={loading || totalPages <= 1}
                >
                    {pages.map((p) => (
                        <option key={p} value={p}>
                            Page {p}
                        </option>
                    ))}
                </select>
            </div>
        </div>
    );
}

/* ---------------- Confirm Modal (animated) ---------------- */
function ConfirmModal({ open, data, onClose, onConfirm, loading }) {
    return (
        <AnimatePresence>
            {open ? (
                <>
                    <motion.div
                        className="cm-backdrop"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        style={{ zIndex: 1050 }}
                    />
                    <motion.div
                        className="cm-wrap"
                        initial={{ opacity: 0, y: 16, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 16, scale: 0.98 }}
                        transition={{ duration: 0.18, ease: "easeOut" }}
                        style={{ zIndex: 1055 }}
                        role="dialog"
                        aria-modal="true"
                    >
                        <div className="cm-panel" onClick={(e) => e.stopPropagation()}>
                            <div className="cm-head">
                                <div>
                                    <div className="fw-bold">Confirm Bonus</div>
                                    <div className="text-muted small">Please verify before submit</div>
                                </div>
                                <button className="btn btn-link p-0 cm-x" type="button" onClick={onClose} aria-label="Close">
                                    <FaTimes />
                                </button>
                            </div>

                            <div className="cm-body">
                                <div className="border rounded-3 p-3 bg-body-tertiary">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <div className="text-muted small">User ID</div>
                                        <div className="fw-semibold">{safeText(data?.user_id)}</div>
                                    </div>
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <div className="text-muted small">Amount</div>
                                        <div className="fw-semibold">{moneyFmt(data?.amount)}</div>
                                    </div>
                                    <div className="d-flex justify-content-between align-items-start">
                                        <div className="text-muted small">Remark</div>
                                        <div className="fw-semibold text-end" style={{ maxWidth: 320, wordBreak: "break-word" }}>
                                            {safeText(data?.remark) === "—" ? <span className="text-muted">—</span> : safeText(data?.remark)}
                                        </div>
                                    </div>
                                </div>

                                <div className="text-muted small mt-2">After confirmation, bonus will be issued immediately.</div>
                            </div>

                            <div className="cm-foot">
                                <button className="btn btn-outline-secondary" type="button" onClick={onClose} disabled={loading}>
                                    Cancel
                                </button>
                                <button className="btn btn-primary" type="button" onClick={onConfirm} disabled={loading}>
                                    {loading ? "Submitting..." : "Confirm & Submit"}
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </>
            ) : null}
        </AnimatePresence>
    );
}

/* ---------------- Details Modal (custom CSS fixed) ---------------- */
function DetailsModal({ open, row, onClose }) {
    const s = row?.bonus || {};
    const u = row?.user || {};

    const userId = safeText(u?.user_id);
    const name = safeText(u?.name);
    const mobileOnly = safeText(u?.mobile);
    const cc = safeText(u?.country_code);
    const mobileDisplay = mobileOnly === "—" ? "—" : `+${cc === "—" ? "" : cc} ${mobileOnly}`.trim();
    const email = safeText(u?.email);

    const amount = safeText(s?.amount);
    const remark = safeText(s?.remark);
    const createDate = formatDateTime(s?.create_date);

    // Scroll-lock (fixes overlay issues on some layouts)
    useEffect(() => {
        if (!open) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = prev;
        };
    }, [open]);

    return (
        <AnimatePresence>
            {open ? (
                <>
                    {/* Backdrop */}
                    <motion.div
                        className="im-backdrop"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        style={{ zIndex: 1060 }}
                    />

                    {/* Wrapper */}
                    <motion.div
                        className="im-wrap"
                        initial={{ opacity: 0, y: 18, scale: 0.985 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 18, scale: 0.985 }}
                        transition={{ duration: 0.2, ease: "easeOut" }}
                        style={{ zIndex: 1065 }}
                        role="dialog"
                        aria-modal="true"
                        onClick={onClose}
                    >
                        {/* Panel */}
                        <div className="im-panel" onClick={(e) => e.stopPropagation()}>
                            {/* Header */}
                            <div className="im-head">
                                <div className="im-head-left">
                                    <div className="im-avatar">
                                        <FaUserCircle />
                                    </div>
                                    <div>
                                        <div className="im-title">Bonus Details</div>
                                        <div className="im-sub">{createDate}</div>
                                    </div>
                                </div>
                                <button className="im-close" type="button" onClick={onClose} aria-label="Close">
                                    <FaTimes />
                                </button>
                            </div>

                            {/* Body (scrollable if needed) */}
                            <div className="im-body">
                                <div className="row g-3">
                                    <div className="col-12 col-lg-6">
                                        <div className="im-card">
                                            <div className="im-card-head">
                                                <span className="im-ico">
                                                    <FaFileInvoiceDollar />
                                                </span>
                                                <span className="im-card-title">Bonus Information</span>
                                            </div>

                                            <div className="im-kv">
                                                <div className="im-k">Amount</div>
                                                <div className="im-v im-amount">{moneyFmt(amount)}</div>
                                            </div>

                                            <div className="im-kv">
                                                <div className="im-k">
                                                    <FaCalendar className="me-2" />
                                                    Date
                                                </div>
                                                <div className="im-v">{createDate}</div>
                                            </div>

                                            <div className="im-kv im-kv-top">
                                                <div className="im-k">
                                                    <FaRegCommentDots className="me-2" />
                                                    Remark
                                                </div>
                                                <div className="im-v">
                                                    <div className={`im-remark ${remark === "—" ? "muted" : ""}`}>
                                                        {remark === "—" ? "No remark provided" : remark}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* User */}
                                    <div className="col-12 col-lg-6">
                                        <div className="im-card">
                                            <div className="im-card-head">
                                                <span className="im-ico text-primary">
                                                    <FaUser />
                                                </span>
                                                <span className="im-card-title">User Information</span>
                                            </div>

                                            <div className="im-kv">
                                                <div className="im-k">
                                                    <FaIdCard className="me-2" />
                                                    User ID
                                                </div>
                                                <div className="im-v d-flex align-items-center justify-content-between gap-2">
                                                    <code className="im-code">{userId}</code>
                                                    <CopyBtn value={userId === "—" ? "" : userId} label="User ID copied" />
                                                </div>
                                            </div>

                                            <div className="im-kv">
                                                <div className="im-k">
                                                    <FaUser className="me-2" />
                                                    Name
                                                </div>
                                                <div className="im-v">{name}</div>
                                            </div>

                                            <div className="im-kv">
                                                <div className="im-k">
                                                    <FaPhone className="me-2" />
                                                    Mobile
                                                </div>
                                                <div className="im-v">{mobileDisplay}</div>
                                            </div>

                                            <div className="im-kv im-kv-top">
                                                <div className="im-k">
                                                    <FaEnvelope className="me-2" />
                                                    Email
                                                </div>
                                                <div className="im-v" style={{ wordBreak: "break-word" }}>
                                                    {email}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Footer */}
                            <div className="im-foot">
                                <button className="btn btn-primary px-4" type="button" onClick={onClose}>
                                    Close
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </>
            ) : null}
        </AnimatePresence>
    );
}

/* ---------------- Page ---------------- */
export default function Bonus() {
    const token = useMemo(() => localStorage.getItem("token") || "", []);

    // Get today's date in YYYY-MM-DD format
    const todayDate = useMemo(() => getTodayDate(), []);

    const [userId, setUserId] = useState("");
    const [amount, setAmount] = useState("");
    const [remark, setRemark] = useState("");

    // Confirm modal state
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [giveLoading, setGiveLoading] = useState(false);

    // List State
    const [listLoading, setListLoading] = useState(true);
    const [pageNo, setPageNo] = useState(1);

    // Filters (typing doesn't call API)
    const [inputQuery, setInputQuery] = useState("");
    const [appliedQuery, setAppliedQuery] = useState("");

    // Set default date to today
    const [inputDate, setInputDate] = useState(todayDate);
    const [appliedDate, setAppliedDate] = useState(todayDate);

    const [bonusList, setBonusList] = useState([]);
    const [info, setInfo] = useState({ total_user: 0, page_no: 1 });

    // Details modal
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);

    const totalPages = useMemo(() => {
        const total = Number(info?.total_user || 0);
        if (!total) return 1;
        return Math.max(1, Math.ceil(total / PAGE_SIZE));
    }, [info?.total_user]);

    const fetchList = useCallback(
        async ({ page, query, date }) => {
            setListLoading(true);
            try {
                const res = await axios({
                    method: "post",
                    url: `${BASE_ENDPOINT}${LIST_ENDPOINT}`,
                    headers: { "Content-Type": "application/json" },
                    data: { token, query: query || "", page_no: page, date: date || "" },
                });

                const data = res?.data;

                if (data?.error) {
                    toast.error(typeof data?.error === "string" ? data.error : "Failed to load bonus list");
                    setBonusList([]);
                    setInfo({ total_user: 0, page_no: page });
                    return;
                }

                const list = Array.isArray(data?.bonus_list) ? data.bonus_list : [];
                setBonusList(list);

                const apiInfo = data?.info || {};
                setInfo({
                    total_user: Number(apiInfo?.total_user || 0),
                    page_no: Number(apiInfo?.page_no || page),
                });
            } catch (err) {
                console.log(err);
                toast.error("Failed to load bonus list");
                setBonusList([]);
                setInfo({ total_user: 0, page_no: page });
            } finally {
                setListLoading(false);
            }
        },
        [token]
    );

    // Initial load with today's date
    useEffect(() => {
        fetchList({ page: 1, query: "", date: todayDate });
    }, [fetchList, todayDate]);

    // Refetch on page/query/date change (applied only)
    useEffect(() => {
        fetchList({ page: pageNo, query: appliedQuery, date: appliedDate });
    }, [pageNo, appliedQuery, appliedDate, fetchList]);

    const onApplyFilters = () => {
        const q = inputQuery.trim();
        const d = toMySQLDate(inputDate);
        setPageNo(1);
        setAppliedQuery(q);
        setAppliedDate(d);
    };

    const onRefreshReset = async () => {
        setInputQuery("");
        setAppliedQuery("");
        // Reset to today's date
        setInputDate(todayDate);
        setAppliedDate(todayDate);
        setPageNo(1);

        setDetailsOpen(false);
        setSelectedRow(null);

        await fetchList({ page: 1, query: "", date: todayDate });
    };

    const onPrev = () => setPageNo((p) => Math.max(1, p - 1));
    const onNext = () => setPageNo((p) => Math.min(totalPages, p + 1));
    const onJump = (p) => setPageNo(Math.min(totalPages, Math.max(1, p)));

    // Confirm flow
    const openConfirm = (e) => {
        e.preventDefault();
        const uid = userId.trim();
        const amt = String(amount).trim();
        if (!uid) return toast.error("Please enter user_id");
        if (!amt || Number(amt) <= 0) return toast.error("Please enter a valid amount");
        setConfirmOpen(true);
    };

    const closeConfirm = () => {
        if (giveLoading) return;
        setConfirmOpen(false);
    };

    const submitBonus = async () => {
        const uid = userId.trim();
        const amt = String(amount).trim();
        const rm = remark.trim();

        if (!uid) return toast.error("Please enter user_id");
        if (!amt || Number(amt) <= 0) return toast.error("Please enter a valid amount");

        setGiveLoading(true);
        try {
            const res = await axios({
                method: "post",
                url: `${BASE_ENDPOINT}${GIVE_ENDPOINT}`,
                headers: { "Content-Type": "application/json" },
                data: { token, user_id: uid, amount: amt, remark: rm || "" },
            });

            const out = res?.data;

            if (out?.error) {
                toast.error(typeof out?.error === "string" ? out.error : "Failed to give bonus");
                return;
            }

            toast.success(out?.msg || "Bonus given successfully");
            setConfirmOpen(false);

            setUserId("");
            setAmount("");
            setRemark("");

            await fetchList({ page: pageNo, query: appliedQuery, date: appliedDate });
        } catch (err) {
            console.log(err);
            toast.error("Failed to give bonus");
        } finally {
            setGiveLoading(false);
        }
    };

    const formConfirmData = useMemo(
        () => ({
            user_id: userId.trim(),
            amount: amount,
            remark: remark.trim() || "—",
        }),
        [userId, amount, remark]
    );

    const openDetails = (row) => {
        setSelectedRow(row);
        setDetailsOpen(true);
    };

    const closeDetails = () => {
        setDetailsOpen(false);
        setSelectedRow(null);
    };

    return (
        <PageTransition>
            <style>{`
        /* -------- General -------- */
        .mono { font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace; }
        .btn-compact { padding: 6px 10px !important; line-height: 1.05 !important; border-radius: 8px; }

        /* -------- Cards / Pills -------- */
        .pill {
          border: 1px solid rgba(0,0,0,0.10);
          border-radius: 14px;
          padding: 12px 16px;
          background: #fff;
          box-shadow: 0 2px 4px rgba(0,0,0,0.05);
        }
        .pill-label { font-size: 0.8rem; color: rgba(0,0,0,0.6); font-weight: 500; letter-spacing: 0.3px; }
        .pill-value { font-size: 1.1rem; font-weight: 800; margin-top: 4px; color: #333; }

        /* -------- Table -------- */
        .tight th, .tight td { padding: 8px 10px !important; vertical-align: middle; }
        .tight th:not(:last-child), .tight td:not(:last-child) { border-right: 1px solid rgba(0,0,0,0.06); }
        .table-hover tbody tr:hover { background-color: rgba(13, 110, 253, 0.03) !important; }

        /* -------- Confirm Modal (cm-*) -------- */
        .cm-backdrop {
          position: fixed; inset: 0;
          background: rgba(0,0,0,0.58);
          backdrop-filter: blur(2px);
        }
        .cm-wrap {
          position: fixed; inset: 0;
          display: flex; align-items: center; justify-content: center;
          padding: 16px;
        }
        .cm-panel {
          width: 100%;
          max-width: 520px;
          background: #fff;
          border-radius: 14px;
          overflow: hidden;
          box-shadow: 0 18px 45px rgba(0,0,0,0.28);
          border: 1px solid rgba(255,255,255,0.10);
        }
        .cm-head {
          padding: 14px 16px;
          display: flex; align-items: flex-start; justify-content: space-between;
          border-bottom: 1px solid rgba(0,0,0,0.06);
          background: #fff;
        }
        .cm-x { color: rgba(0,0,0,0.55); }
        .cm-x:hover { color: rgba(0,0,0,0.85); }
        .cm-body { padding: 14px 16px; }
        .cm-foot {
          padding: 12px 16px;
          display: flex; justify-content: flex-end; gap: 10px;
          border-top: 1px solid rgba(0,0,0,0.06);
          background: #fafafa;
        }

        /* -------- Info Modal (im-*) — FIXED OVERLAY + PANEL -------- */
        .im-backdrop{
          position: fixed; inset: 0;
          background: rgba(10, 12, 16, 0.68);
          backdrop-filter: blur(3px);
        }

        .im-wrap{
          position: fixed; inset: 0;
          display: flex; align-items: center; justify-content: center;
          padding: 18px;
          overflow: auto; /* if screen is short, allow wrapper to scroll */
        }

        .im-panel{
          width: 100%;
          max-width: 980px;
          background: #fff;
          border-radius: 16px;
          overflow: hidden;
          box-shadow: 0 22px 60px rgba(0,0,0,0.35);
          border: 1px solid rgba(255,255,255,0.12);
        }

        .im-head{
          padding: 14px 16px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: #fff;
          display: flex; align-items: center; justify-content: space-between;
        }
        .im-head-left{ display: flex; align-items: center; gap: 12px; }
        .im-avatar{
          width: 42px; height: 42px;
          border-radius: 12px;
          display: flex; align-items: center; justify-content: center;
          background: rgba(255,255,255,0.18);
          font-size: 22px;
        }
        .im-title{ font-weight: 800; letter-spacing: 0.2px; }
        .im-sub{ font-size: 12px; color: rgba(255,255,255,0.85); margin-top: 2px; }

        .im-close{
          border: 0;
          background: rgba(255,255,255,0.14);
          color: rgba(255,255,255,0.92);
          width: 36px; height: 36px;
          border-radius: 10px;
          display: inline-flex; align-items: center; justify-content: center;
          transition: background .12s ease;
        }
        .im-close:hover{ background: rgba(255,255,255,0.22); }

        .im-body{
          padding: 14px 14px 10px 14px;
          max-height: calc(100vh - 170px); /* prevents overflow under header/footer */
          overflow: auto; /* this is key to keep modal usable */
          background: #fff;
        }

        .im-card{
          background: #f8f9fa;
          border: 1px solid rgba(0,0,0,0.06);
          border-radius: 14px;
          padding: 14px;
          height: 100%;
        }
        .im-card-head{
          display: flex; align-items: center; gap: 10px;
          margin-bottom: 12px;
        }
        .im-ico{
          width: 34px; height: 34px;
          border-radius: 10px;
          display: inline-flex; align-items: center; justify-content: center;
          background: rgba(108,117,125,0.12);
          color: #6c757d;
        }
        .im-card-title{ font-weight: 800; color: rgba(0,0,0,0.80); }

        .im-kv{ display: flex; justify-content: space-between; gap: 10px; margin-bottom: 10px; }
        .im-kv-top{ align-items: flex-start; }
        .im-k{ font-size: 12px; color: rgba(0,0,0,0.55); font-weight: 600; white-space: nowrap; }
        .im-v{ font-weight: 700; color: rgba(0,0,0,0.85); text-align: right; }
        .im-amount{ font-size: 22px; color: #198754; }

        .im-code{
          font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
          font-weight: 800;
          background: #fff;
          border: 1px solid rgba(0,0,0,0.08);
          padding: 6px 10px;
          border-radius: 10px;
          font-size: 0.9rem;
        }

        .im-remark{
          background: #fff;
          border: 1px solid rgba(0,0,0,0.08);
          border-radius: 12px;
          padding: 10px 12px;
          min-height: 78px;
          text-align: left;
          word-break: break-word;
        }
        .im-remark.muted{
          background: rgba(0,0,0,0.03);
          color: rgba(0,0,0,0.55);
          border-color: rgba(0,0,0,0.06);
        }

        .im-foot{
          padding: 12px 14px;
          border-top: 1px solid rgba(0,0,0,0.06);
          background: #fafafa;
          display: flex;
          justify-content: flex-end;
        }

        @media (max-width: 575.98px){
          .im-panel{ border-radius: 14px; }
          .im-body{ max-height: calc(100vh - 190px); }
          .im-k{ white-space: normal; }
          .im-v{ text-align: left; }
          .im-kv{ flex-direction: column; align-items: flex-start; }
        }
      `}</style>

            {/* Header */}
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                <div>
                    <h2 className="fw-bold mb-1" style={{ color: "#333" }}>
                        Bonus Management
                    </h2>
                    <div className="text-muted">Give bonus and view bonus records</div>
                </div>

                <button
                    type="button"
                    className="btn btn-outline-primary d-inline-flex align-items-center gap-2"
                    onClick={onRefreshReset}
                    disabled={listLoading}
                    title="Reset & Refresh"
                    style={{ borderRadius: "8px" }}
                >
                    <FaSyncAlt />
                    {listLoading ? "Refreshing..." : "Refresh"}
                </button>
            </div>

            <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: "10px" }}>
                <div className="card-body">
                    <div className="d-flex align-items-center gap-2 mb-3">
                        <div
                            className="rounded-3 d-flex align-items-center justify-content-center"
                            style={{
                                width: 42,
                                height: 42,
                                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                                color: "white",
                            }}
                        >
                            <FaMoneyBillWave />
                        </div>
                        <div>
                            <div className="fw-semibold fs-5">Give Bonus</div>
                            <div className="text-muted small">User ID, Amount, Remark (optional)</div>
                        </div>
                    </div>

                    <form onSubmit={openConfirm}>
                        <div className="row g-3 align-items-end">
                            <div className="col-12 col-md-4">
                                <label className="form-label small text-muted mb-1">User ID</label>
                                <div className="input-group">
                                    <span className="input-group-text bg-light">
                                        <FaUser className="text-secondary" />
                                    </span>
                                    <input
                                        className="form-control"
                                        placeholder="Enter user_id"
                                        value={userId}
                                        onChange={(e) => setUserId(e.target.value)}
                                        style={{ borderRadius: "0 6px 6px 0" }}
                                    />
                                </div>
                            </div>

                            <div className="col-12 col-md-3">
                                <label className="form-label small text-muted mb-1">Amount</label>
                                <div className="input-group">
                                    <span className="input-group-text bg-light">
                                        <FaRupeeSign className="text-secondary" />
                                    </span>
                                    <input
                                        type="number"
                                        step="0.01"
                                        className="form-control"
                                        placeholder="Enter amount"
                                        value={amount}
                                        onChange={(e) => setAmount(e.target.value)}
                                        style={{ borderRadius: "0 6px 6px 0" }}
                                    />
                                </div>
                            </div>

                            <div className="col-12 col-md-3">
                                <label className="form-label small text-muted mb-1">Remark (optional)</label>
                                <div className="input-group">
                                    <span className="input-group-text bg-light">
                                        <FaRegCommentDots className="text-secondary" />
                                    </span>
                                    <input
                                        className="form-control"
                                        placeholder="Remark"
                                        value={remark}
                                        onChange={(e) => setRemark(e.target.value)}
                                        style={{ borderRadius: "0 6px 6px 0" }}
                                    />
                                </div>
                            </div>

                            <div className="col-12 col-md-2 d-grid">
                                <button type="submit" className="btn btn-primary" disabled={giveLoading} style={{ borderRadius: "6px", padding: "10px" }}>
                                    {giveLoading ? "Submitting..." : "Submit"}
                                </button>
                            </div>
                        </div>

                        <div className="text-muted small mt-3">Token is read from localStorage.</div>
                    </form>
                </div>
            </div>

            {/* Quick Stats */}
            <div className="row g-3 mb-4">
                <div className="col-12 col-md-6">
                    <div className="pill">
                        <div className="pill-label">Total Records</div>
                        <div className="pill-value">{nfmt(info?.total_user || 0)}</div>
                    </div>
                </div>
                <div className="col-12 col-md-6">
                    <div className="pill">
                        <div className="pill-label">Active Filters</div>
                        <div className="pill-value d-flex flex-wrap gap-2">
                            {appliedQuery ? (
                                <span className="badge bg-primary px-3 py-2">Query: {appliedQuery}</span>
                            ) : (
                                <span className="badge bg-light text-dark px-3 py-2">Query: —</span>
                            )}
                            {appliedDate ? (
                                <span className="badge bg-success px-3 py-2">Date: {appliedDate}</span>
                            ) : (
                                <span className="badge bg-light text-dark px-3 py-2">Date: —</span>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Filters + Pagination (Top) */}
            <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: "10px" }}>
                <div className="card-body">
                    <div className="row g-3 align-items-end">
                        <div className="col-12 col-lg-6">
                            <label className="form-label small text-muted mb-1">Search</label>
                            <div className="input-group">
                                <span className="input-group-text bg-light">
                                    <FaSearch className="text-secondary" />
                                </span>
                                <input
                                    className="form-control"
                                    placeholder="Search (runs only on Apply Filters)"
                                    value={inputQuery}
                                    onChange={(e) => setInputQuery(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") onApplyFilters();
                                    }}
                                    style={{ borderRadius: "0 6px 6px 0" }}
                                />
                            </div>
                        </div>

                        <div className="col-12 col-lg-3">
                            <label className="form-label small text-muted mb-1">Date (optional)</label>
                            <div className="input-group">
                                <span className="input-group-text bg-light">
                                    <FaCalendarAlt className="text-secondary" />
                                </span>
                                <input
                                    type="date"
                                    className="form-control"
                                    value={inputDate}
                                    onChange={(e) => setInputDate(e.target.value)}
                                    style={{ borderRadius: "0 6px 6px 0" }}
                                />
                            </div>
                        </div>

                        <div className="col-12 col-lg-3 d-grid">
                            <button type="button" className="btn btn-primary" onClick={onApplyFilters} disabled={listLoading} style={{ borderRadius: "6px", padding: "10px" }}>
                                Apply Filters
                            </button>
                        </div>
                    </div>

                    <div className="mt-4">
                        <PaginationBar loading={listLoading} pageNo={pageNo} totalPages={totalPages} onPrev={onPrev} onNext={onNext} onJump={onJump} />
                    </div>
                </div>
            </div>

            {/* Bonus List */}
            <div className="card border-0 shadow-sm" style={{ borderRadius: "10px", overflow: "hidden" }}>
                <div className="card-body p-0">
                    <div className="p-3 table-responsive">
                        <table className="table table-sm align-middle mb-0 tight table-hover">
                            <thead className="table-light">
                                <tr>
                                    <th style={{ width: 220 }}>Date</th>
                                    <th style={{ width: 130 }} className="text-end">
                                        Amount
                                    </th>
                                    <th style={{ width: 150 }}>User ID</th>
                                    <th style={{ width: 200 }}>Name</th>
                                    <th style={{ width: 180 }}>Mobile</th>
                                    <th style={{ width: 250 }}>Email</th>
                                    <th style={{ width: 80, textAlign: "right" }}>Details</th>
                                </tr>
                            </thead>

                            <tbody>
                                {listLoading ? (
                                    <tr>
                                        <td colSpan={7} className="text-center text-muted py-5">
                                            <div className="spinner-border spinner-border-sm text-primary me-2" role="status"></div>
                                            Loading bonus records…
                                        </td>
                                    </tr>
                                ) : bonusList.length === 0 ? (
                                    <tr>
                                        <td colSpan={7} className="text-center text-muted py-5">
                                            <div className="fw-semibold">No bonus records found</div>
                                            <div className="small">Try changing filters or page.</div>
                                        </td>
                                    </tr>
                                ) : (
                                    bonusList.map((row, idx) => {
                                        const s = row?.bonus || {};
                                        const u = row?.user || {};

                                        const date = formatDateTime(s?.create_date);
                                        const amt = safeText(s?.amount);

                                        const uid = safeText(u?.user_id);
                                        const name = safeText(u?.name);

                                        const mobileOnly = safeText(u?.mobile);
                                        const cc = safeText(u?.country_code);
                                        const mobileDisplay = mobileOnly === "—" ? "—" : `+${cc === "—" ? "" : cc} ${mobileOnly}`.trim();

                                        const email = safeText(u?.email);

                                        return (
                                            <tr key={`${date}-${uid}-${idx}`}>
                                                <td className="text-muted">{date}</td>

                                                <td className="text-end fw-bold text-success">{moneyFmt(amt)}</td>

                                                <td>
                                                    <div className="d-flex align-items-center justify-content-between gap-2">
                                                        <code className="mono fw-semibold bg-light px-2 py-1 rounded" style={{ fontSize: "0.85rem" }}>
                                                            {uid}
                                                        </code>
                                                        <CopyBtn value={uid === "—" ? "" : uid} label="User ID copied" />
                                                    </div>
                                                </td>

                                                <td className="fw-semibold" style={{ wordBreak: "break-word" }}>
                                                    {name}
                                                </td>

                                                <td className="fw-semibold">{mobileDisplay}</td>

                                                <td className="fw-semibold text-break">{email}</td>

                                                <td style={{ textAlign: "right" }}>
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-outline-primary btn-compact"
                                                        onClick={() => openDetails(row)}
                                                        title="View details"
                                                    >
                                                        <FaInfoCircle />
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Bottom Pagination */}
                    <div className="px-3 py-3 border-top bg-light">
                        <PaginationBar loading={listLoading} pageNo={pageNo} totalPages={totalPages} onPrev={onPrev} onNext={onNext} onJump={onJump} />
                    </div>
                </div>
            </div>

            {/* Confirm Modal */}
            <ConfirmModal open={confirmOpen} data={formConfirmData} onClose={closeConfirm} onConfirm={submitBonus} loading={giveLoading} />

            {/* Details Modal (fixed overlay) */}
            <DetailsModal open={detailsOpen} row={selectedRow} onClose={closeDetails} />
        </PageTransition>
    );
}